import React, { ReactElement, useEffect, useMemo, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import logo from '../../assets/images/logo.svg'
import { logger } from '../../utils'
import { Formik, Form, Field, FieldArray, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Button, Input, Text } from '../atoms'
import { ModalStripe } from '../molecules'
import ContactLinks from '../molecules/ContactLinks/ContactLinks'
import { toast } from 'react-toastify'

// --- Types ---
interface StudentData {
  email: string
  firstName: string
  lastName: string
  submittedAt?: string
}

interface SessionDetails {
  quantity: number
  paymentStatus: string
  customerEmail?: string
  flow?: string
  studentsSubmitted: number
  existingStudents: StudentData[]
  remainingSlots: number
  studentCollectionStatus: 'pending' | 'partial' | 'complete'
  productName: string
}

interface CouponInfo {
  code: string
  discountPercentage: number
}

type SessionLookupStatus = 'idle' | 'loading' | 'success' | 'error' | 'payment_pending'

interface StudentFormValues {
  students: StudentData[]
}

// --- API Functions ---
const CHECKOUT_ENDPOINT = 'https://aote22c7zh.execute-api.us-east-2.amazonaws.com/default/stripeCheckout'
const WEBHOOK_ENDPOINT = 'https://buxj4tmda4.execute-api.us-east-2.amazonaws.com/default/stripeWebHook'

const fetchSessionDetails = async (sessionId: string, signal?: AbortSignal): Promise<{ success: boolean, data?: SessionDetails, error?: string, code?: string }> => {
  try {
    const res = await fetch(CHECKOUT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'GET_SESSION_DETAILS', sessionId }),
      signal
    })
    const data = await res.json()
    if (data.success === true) {
      return {
        success: true,
        data: {
          quantity: data.quantity,
          paymentStatus: data.paymentStatus,
          customerEmail: data.customerEmail,
          flow: data.flow,
          studentsSubmitted: data.studentsSubmitted ?? 0,
          existingStudents: data.existingStudents ?? [],
          remainingSlots: data.remainingSlots ?? data.quantity,
          studentCollectionStatus: data.studentCollectionStatus ?? 'pending',
          productName: data.productName ?? 'DopaGE Prevention Portal - 1 year'
        }
      }
    }
    return { success: false, error: data.error ?? 'Failed to fetch session details', code: data.code }
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      return { success: false, error: 'Request aborted' }
    }
    return { success: false, error: 'Network error' }
  }
}

const submitStudents = async (sessionId: string, studentData: StudentData[]): Promise<{ success: boolean, message?: string, error?: string, code?: string }> => {
  try {
    const res = await fetch(CHECKOUT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'UPDATE_SESSION_STUDENTS',
        sessionId,
        studentData
      })
    })
    return await res.json()
  } catch (err) {
    return { success: false, error: 'Network error submitting students' }
  }
}

// --- Validation Schema ---
const StudentSchema = Yup.object().shape({
  students: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email('Invalid email format').required('Email is required'),
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required')
    })
  ).min(1, 'At least one student is required')
})

// --- Student Collection Form Component (Form Fields Only) ---
interface StudentFormProps {
  sessionId: string
  sessionDetails: SessionDetails
  onSubmitSuccess: () => void
  onFormStateChange: (isSubmitting: boolean, isValid: boolean) => void
}

// Form state tracker component to handle side effects properly
const FormStateTracker = ({ isSubmitting, isValid, onFormStateChange }: { isSubmitting: boolean, isValid: boolean, onFormStateChange: (isSubmitting: boolean, isValid: boolean) => void }): null => {
  useEffect(() => {
    onFormStateChange(isSubmitting, isValid)
  }, [isSubmitting, isValid, onFormStateChange])
  return null
}

const StudentCollectionForm = ({ sessionId, sessionDetails, onSubmitSuccess, onFormStateChange }: StudentFormProps): ReactElement => {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const initialStudents: StudentData[] = Array.from(
    { length: sessionDetails.remainingSlots },
    () => ({ email: '', firstName: '', lastName: '' })
  )

  const handleSubmit = async (values: StudentFormValues, actions: FormikHelpers<StudentFormValues>): Promise<void> => {
    setSubmitError(null)
    actions.setSubmitting(true)

    try {
      const result = await submitStudents(sessionId, values.students)
      if (result.success) {
        toast.success('Students registered successfully!', { position: toast.POSITION.TOP_CENTER })
        onSubmitSuccess()
      } else {
        const errorMsg = result.error ?? 'Failed to register students'
        setSubmitError(errorMsg)
        toast.error(errorMsg, { position: toast.POSITION.TOP_CENTER })
      }
    } catch (err) {
      const errorMsg = 'An unexpected error occurred'
      setSubmitError(errorMsg)
      toast.error(errorMsg, { position: toast.POSITION.TOP_CENTER })
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{ students: initialStudents }}
      validationSchema={StudentSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ values, errors, touched, isSubmitting, isValid }: FormikProps<StudentFormValues>) => {
        return (
          <>
            <FormStateTracker isSubmitting={isSubmitting} isValid={isValid} onFormStateChange={onFormStateChange} />

            <Form id='student-collection-form' className='w-full'>
              <FieldArray name='students'>
                {() => (
                  <div className='space-y-4'>
                    {values.students.map((_, index) => {
                      const studentNumber = sessionDetails.studentsSubmitted + index + 1
                      const studentErrors = errors.students?.[index] as StudentData | undefined
                      const studentTouched = touched.students?.[index]

                      return (
                        <div key={index} className='p-3 sm:p-4 border border-[#2B2D2C] rounded-md' style={{ backgroundColor: '#DBE3DF' }}>
                          <label className='block text-xs sm:text-sm font-medium text-[#3E4140] mb-2'>
                            Student #{studentNumber}
                          </label>

                          {/* Email Field */}
                          <div className='mb-2'>
                            <Field name={`students.${index}.email`}>
                              {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                                <Input
                                  id={`students.${index}.email`}
                                  data-testid={`input-student-email-${index}`}
                                  className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                                  style={{ borderColor: '#2B2D2C', color: '#101111' }}
                                  type='email'
                                  placeholder='Email Address'
                                  autoComplete='off'
                                  {...field}
                                />
                              )}
                            </Field>
                            {studentErrors?.email != null && studentTouched?.email === true && (
                              <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.email}</div>
                            )}
                          </div>

                          {/* Name Fields Row */}
                          <div className='flex flex-col sm:flex-row sm:space-x-3 space-y-2 sm:space-y-0'>
                            <div className='w-full sm:w-1/2'>
                              <Field name={`students.${index}.firstName`}>
                                {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                                  <Input
                                    id={`students.${index}.firstName`}
                                    data-testid={`input-student-firstname-${index}`}
                                    className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                                    style={{ borderColor: '#2B2D2C', color: '#101111' }}
                                    type='text'
                                    placeholder='First Name'
                                    autoComplete='off'
                                    {...field}
                                  />
                                )}
                              </Field>
                              {studentErrors?.firstName != null && studentTouched?.firstName === true && (
                                <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.firstName}</div>
                              )}
                            </div>
                            <div className='w-full sm:w-1/2'>
                              <Field name={`students.${index}.lastName`}>
                                {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                                  <Input
                                    id={`students.${index}.lastName`}
                                    data-testid={`input-student-lastname-${index}`}
                                    className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                                    style={{ borderColor: '#2B2D2C', color: '#101111' }}
                                    type='text'
                                    placeholder='Last Name'
                                    autoComplete='off'
                                    {...field}
                                  />
                                )}
                              </Field>
                              {studentErrors?.lastName != null && studentTouched?.lastName === true && (
                                <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.lastName}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </FieldArray>

              {submitError != null && (
                <div className='mt-4 p-3 bg-red-100 border border-red-400 rounded text-center text-sm text-[#ff674c]' aria-live='polite'>
                  {submitError}
                </div>
              )}
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

// --- Submit Button Component (renders separately with form link) ---
interface StudentSubmitButtonProps {
  isSubmitting: boolean
  isValid: boolean
}

const StudentSubmitButton = ({ isSubmitting, isValid }: StudentSubmitButtonProps): ReactElement => {
  return (
    <div className='w-full max-w-full mx-auto mt-6'>
      <div>
        <Button
          id='button-complete-setup'
          data-testid='button-complete-setup'
          form='student-collection-form'
          className='diamond-btn min-h-[48px] sm:h-[52px] w-full font-bold flex justify-center items-center bg-[#1A7A52] text-[#F2F6F4] disabled:opacity-50 disabled:cursor-not-allowed'
          type='submit'
          loading={isSubmitting}
          disabled={isSubmitting || !isValid}
          aria-label='Complete student setup'
        >
          <Text className='text-[#F2F6F4] text-base sm:text-lg' size='BASE' text='Complete Setup ➔' type='span' />
        </Button>
      </div>

      <div className='mt-3 text-center'>
        <Text
          className='text-[#3E4140] text-xs'
          size='SM'
          text='Need to add students later? Save this page as a Bookmark!'
        />
      </div>
      <div className='mt-4 w-full max-w-sm mx-auto'>
        <ContactLinks id='link-contact-success' textColor='text-[#3E4140]' linkColor='text-[#1A7A52]' />
      </div>
    </div>
  )
}

// --- Completion State Component ---
interface CompletionStateProps {
  existingStudents: StudentData[]
  couponCode?: string | null
  discountAmount?: string
}

const CompletionState = ({ existingStudents, couponCode, discountAmount = '$5.00' }: CompletionStateProps): ReactElement => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')

  const handleCopyCode = useCallback(async (code: string): Promise<void> => {
    try {
      if (navigator?.clipboard?.writeText != null) {
        await navigator.clipboard.writeText(code)
      } else {
        // Fallback for older browsers
        const tempInput = document.createElement('textarea')
        tempInput.value = code
        tempInput.style.position = 'fixed'
        tempInput.style.opacity = '0'
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand('copy')
        document.body.removeChild(tempInput)
      }
      setCopyState('copied')
      toast.success('Code copied to clipboard!', { position: toast.POSITION.TOP_CENTER })
      setTimeout(() => { setCopyState('idle') }, 1500)
    } catch (_err) {
      setCopyState('error')
      toast.error('Failed to copy code', { position: toast.POSITION.TOP_CENTER })
      setTimeout(() => { setCopyState('idle') }, 1500)
    }
  }, [])

  return (
    <div className='w-full max-w-full mx-auto'>
      {/* Section 1: Header */}
      <div className='mb-6 text-center'>
        <h1 className='font-bold text-xl sm:text-2xl md:text-3xl' style={{ color: '#1A7A52' }}>
          TRAINING INITIATED
        </h1>
      </div>

      {/* Section 2: Confirmation Checkmark */}
      <div className='mb-4'>
        <Text
          className='text-[#101111] text-base sm:text-lg font-semibold'
          text='✓ Your students are now registered!'
          type='paragraph'
        />
      </div>

      {/* Section 3: Instructions Header */}
      <div className='mb-4'>
        <Text
          className='text-[#3E4140] text-sm sm:text-base'
          text='Sign-on access and instructions have been sent to:'
          type='paragraph'
        />
      </div>

      {/* Section 4: Student Email List */}
      <div className='mb-6 space-y-2'>
        {existingStudents.map((student, index) => (
          <div key={index} className='text-sm sm:text-base pl-6 sm:pl-8' style={{ color: '#1A7A52' }}>
            {student.email}
          </div>
        ))}
      </div>

      {/* Section 5: What Happens Next */}
      <div className='mb-6'>
        <h2 className='text-[#101111] text-base sm:text-lg font-semibold mb-3'>
          What happens next?
        </h2>
        <ol className='space-y-3 text-sm sm:text-base list-decimal pl-6' style={{ color: '#3E4140' }}>
          <li className='text-[#101111]'>Your student receives an email with their unique sign-on link</li>
          <li className='text-[#101111]'>They create their password and start training</li>
          <li className='text-[#101111]'>
            They can return to their training and resources anytime in the next 12 months at{' '}
            <a
              href='https://train.dopa.ge'
              target='_blank'
              rel='noopener noreferrer'
              className='underline hover:text-dopage-darkGreen transition-colors'
              style={{ color: '#1A7A52' }}
            >
              train.dopa.ge
            </a>
          </li>
        </ol>
      </div>

      {/* Section 6: Motivational Message */}
      <div className='mb-6 text-center'>
        <p className='text-base sm:text-lg font-semibold leading-relaxed text-center' style={{ color: '#1A7A52' }}>
          You just purchased something priceless: the power to save a life.
        </p>
      </div>

      {/* Section 7: Referral Code Box (Conditional) */}
      {couponCode != null && couponCode !== '' && (
        <div
          className='mb-6 p-4 sm:p-6 rounded-lg text-center'
          style={{
            backgroundColor: '#DBE3DF',
            border: '1px solid #2B2D2C',
            boxShadow: '0 0 10px rgba(26, 122, 82, 0.1)'
          }}
        >
          <h3 className='text-[#101111] text-base sm:text-lg font-semibold mb-3'>
            Share with friends!
          </h3>

          <div className='flex items-center justify-center gap-2 mb-3'>
            <span className='text-[#101111] text-lg sm:text-xl'>Code:</span>
            <span className='text-lg sm:text-xl font-bold tracking-wider' style={{ color: '#1A7A52' }}>
              {couponCode}
            </span>
            <button
              onClick={() => { void handleCopyCode(couponCode) }}
              className='text-2xl hover:scale-110 transition-transform cursor-pointer'
              aria-label='Copy referral code to clipboard'
              disabled={copyState === 'copied'}
              title={copyState === 'copied' ? 'Copied!' : 'Copy code'}
            >
              {copyState === 'copied' ? '✓' : '📋'}
            </button>
          </div>

          <Text
            className='text-[#3E4140] text-sm sm:text-base'
            text={`${discountAmount} off each student`}
            type='paragraph'
          />
        </div>
      )}

      {/* Section 8: Footer - Contact Links */}
      <div className='mt-6 text-center'>
        <ContactLinks id='link-contact-completion' textColor='text-[#3E4140]' linkColor='text-[#1A7A52]' />
      </div>
    </div>
  )
}

// --- Main Payment Success Component ---
function PaymentSuccessPage (): ReactElement {
  const [searchParams] = useSearchParams()
  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams])

  // Session details state
  const [sessionLookupStatus, setSessionLookupStatus] = useState<SessionLookupStatus>('idle')
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)

  // Coupon state (existing functionality)
  const [coupon, setCoupon] = useState<CouponInfo | null>(null)

  // Fetch session details
  const loadSessionDetails = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (sessionId == null || sessionId === '') return

    setSessionLookupStatus('loading')
    setSessionError(null)

    const result = await fetchSessionDetails(sessionId, signal)

    if (result.success && result.data != null) {
      setSessionDetails(result.data)
      setSessionLookupStatus('success')
    } else if (result.code === 'payment_pending') {
      setSessionLookupStatus('payment_pending')
      setSessionError('Payment is still being processed. Please wait a moment and refresh.')
    } else {
      setSessionLookupStatus('error')
      setSessionError(result.error ?? 'Failed to load session details')
    }
  }, [sessionId])

  useEffect(() => {
    const controller = new AbortController()

    if (sessionId != null && sessionId !== '') {
      logger.log(`Payment success page loaded for session ID: ${sessionId}`)
      void loadSessionDetails(controller.signal)
    } else {
      logger.error('Payment success page loaded without a session ID.')
      setSessionLookupStatus('error')
      setSessionError('No session ID provided')
    }

    return () => {
      controller.abort()
    }
  }, [sessionId, loadSessionDetails])

  // Fetch coupon info (existing functionality)
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchDiscount = async (): Promise<void> => {
      if (sessionId == null || sessionId === '') return
      try {
        const res = await fetch(WEBHOOK_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'CHECKOUT_SESSION_LOOKUP', sessionId }),
          signal: controller.signal
        })
        const data = await res.json()
        if (!isMounted) return
        if (data?.success === true && data.coupon?.code != null) {
            setCoupon({ code: data.coupon.code, discountPercentage: Number(data.coupon.discountPercentage) || 0 })
        }
      } catch (_err) {
        // Silently fail for coupon lookup
      }
    }

    void fetchDiscount()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [sessionId])

  const handleStudentSubmitSuccess = useCallback((): void => {
    // Reload session details to get updated state
    void loadSessionDetails()
  }, [loadSessionDetails])

  // Add custom styles and global body overrides
  React.useEffect(() => {
    // 1. Component-specific styles
    const styleEl = document.createElement('style')
    styleEl.innerHTML = `
            .payment-success-container .diamond-corner > div.inner {
                background-color: #F2F6F4 !important;
            }
            .payment-success-container .diamond-corner {
                background-color: #DBE3DF !important;
            }
            .payment-success-container > div > div {
                background-color: transparent !important;
            }
        `
    document.head.appendChild(styleEl)

    // 2. Global body/html overrides for scrolling
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.body.style.minHeight = '100vh'
    document.body.style.position = 'relative'

    return () => {
      // Cleanup styles
      if (styleEl.parentNode === document.head) {
        document.head.removeChild(styleEl)
      }
      // Cleanup global overrides
      document.documentElement.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('min-height')
      document.body.style.removeProperty('position')
    }
  }, [])

  // Determine what to show based on session state
  const isComplete = sessionDetails?.studentCollectionStatus === 'complete'
  // Show student collection form for ANY paid session with remaining slots
  // This covers both deferred flow (new) and immediate flow (old) where students need to be added
  const needsStudentCollection = sessionDetails != null &&
    sessionDetails.remainingSlots > 0 &&
    sessionDetails.paymentStatus === 'paid'

  // Track form state for submit button
  const [formState, setFormState] = useState({ isSubmitting: false, isValid: false })

  const handleFormStateChange = useCallback((isSubmitting: boolean, isValid: boolean) => {
    setFormState({ isSubmitting, isValid })
  }, [])

  return (
    <div
      className='min-h-screen overflow-y-auto relative bg-[#101111]'
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Logo */}
      <header className='flex justify-center pt-4 pb-1 sm:absolute sm:left-6 sm:top-6 sm:pt-0 sm:pb-0 md:left-[40px] md:top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[80px] sm:w-[120px] md:w-[138px]' alt='logo' />
      </header>

      <div className='pt-2 sm:pt-10 pb-8 payment-success-container max-w-[728px] w-full mx-auto flex justify-center px-3 sm:px-6'>

        <div style={{
          filter: 'drop-shadow(0 8px 32px rgba(26, 122, 82, 0.15)) drop-shadow(0 4px 16px rgba(26, 122, 82, 0.1))',
          width: '100%'
        }}>
          <ModalStripe tall>
            {/* Header Section - Only show during student collection */}
            {!isComplete && (
              <>
                <Text
                  className='font-bold text-center text-xl sm:text-2xl text-[#1A7A52] mb-4'
                  bold
                  size='LG'
                  text='ALMOST DONE'
                  type='paragraph'
                />

                <div className='w-full flex flex-col items-center'>
                  {/* Success Confirmation */}
                  <div className='mb-2 w-full max-w-full'>
                    <Text
                      className='text-[#101111] text-base text-left font-semibold'
                      text='✓ Payment successful!'
                      type='paragraph'
                    />
                  </div>

                  {/* Instruction */}
                  <div className='mb-4 w-full max-w-full'>
                    <Text
                      className='text-[#3E4140] text-base text-left'
                      text="Enter your student's name and email to send them instant access:"
                      type='paragraph'
                    />
                  </div>
                </div>
              </>
            )}

          <div className='w-full flex flex-col items-center'>
            {/* Loading State */}
            {sessionLookupStatus === 'loading' && (
              <div className='text-center text-[#3E4140] text-sm py-8'>
                Loading session details...
              </div>
            )}

            {/* Error State */}
            {sessionLookupStatus === 'error' && (
              <div className='text-center py-4'>
                <div className='text-red-400 text-sm mb-4'>{sessionError}</div>
                <Button
                  onClick={() => { void loadSessionDetails() }}
                  className='diamond-btn min-h-[40px] px-6 font-bold flex justify-center items-center bg-[#1A7A52] text-[#F2F6F4]'
                >
                  <Text className='text-[#F2F6F4] text-sm' text='Retry' />
                </Button>
              </div>
            )}

            {/* Payment Pending State */}
            {sessionLookupStatus === 'payment_pending' && (
              <div className='text-center py-4'>
                <div className='text-yellow-400 text-sm mb-4'>{sessionError}</div>
                <Button
                  onClick={() => { void loadSessionDetails() }}
                  className='diamond-btn min-h-[40px] px-6 font-bold flex justify-center items-center bg-[#1A7A52] text-[#F2F6F4]'
                >
                  <Text className='text-[#F2F6F4] text-sm' text='Check Again' />
                </Button>
              </div>
            )}

            {/* Student Collection Form */}
            {sessionLookupStatus === 'success' && needsStudentCollection && sessionId != null && (
              <div className='w-full max-w-full'>
                <StudentCollectionForm
                  sessionId={sessionId}
                  sessionDetails={sessionDetails}
                  onSubmitSuccess={handleStudentSubmitSuccess}
                  onFormStateChange={handleFormStateChange}
                />
              </div>
            )}

            {/* Completion State */}
            {sessionLookupStatus === 'success' && isComplete && sessionDetails != null && (
              <CompletionState
                existingStudents={sessionDetails.existingStudents}
                couponCode={coupon?.code}
                discountAmount='$5.00'
              />
            )}

            {/* Legacy flow - show original message only if NO remaining slots and not complete */}
            {sessionLookupStatus === 'success' && sessionDetails != null && !needsStudentCollection && !isComplete && (
              <div className='mt-4 sm:mt-6 text-center max-w-full'>
              <Text
                  className='text-[#101111] text-sm sm:text-base md:text-lg leading-relaxed'
                  size='SM'
                text='Sign-on instructions have been sent directly to the student email(s) you provided. Students should check their inboxes for a message from DopaGE.'
              />
            </div>
                )}


            {/* Submit Button Section - Rendered here but linked to form above via form attribute */}
            {sessionLookupStatus === 'success' && needsStudentCollection && sessionId != null && (
              <StudentSubmitButton isSubmitting={formState.isSubmitting} isValid={formState.isValid} />
            )}

          </div>
        </ModalStripe>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccessPage

